/**
 * Best-effort, in-browser media probe for the upload pre-flight estimate (Phase 5).
 * Resolution + duration come from a <video>/<audio> element; the video codec from a
 * small MP4-box scan (locate `moov` — handling moov-at-end without reading `mdat` — and
 * look for the stsd sample-entry fourcc). The SERVER ffprobe is the authority; this only
 * warns the operator BEFORE a (possibly large) upload so they can cancel and prepare a
 * matching file instead. Unknown/non-MP4 → codec null → "server will decide".
 */
export interface MediaProbe {
  isAudio: boolean;
  width: number | null;
  height: number | null;
  durationS: number | null;
  codec: 'h264' | 'hevc' | 'other' | null; // null = couldn't determine client-side
}

const AUDIO_EXT = /\.(mp3|m4a|aac|wav|flac|ogg)$/i;

export async function probeMedia(file: File): Promise<MediaProbe> {
  const isAudio = file.type.startsWith('audio/') || AUDIO_EXT.test(file.name);
  const [dims, codec] = await Promise.all([
    probeDims(file, isAudio),
    isAudio ? Promise.resolve(null) : probeCodec(file),
  ]);
  return { isAudio, width: dims.width, height: dims.height, durationS: dims.durationS, codec };
}

function probeDims(
  file: File, isAudio: boolean,
): Promise<{ width: number | null; height: number | null; durationS: number | null }> {
  return new Promise((resolve) => {
    const el = document.createElement(isAudio ? 'audio' : 'video') as HTMLVideoElement;
    const url = URL.createObjectURL(file);
    const done = (w: number | null, h: number | null, d: number | null) => {
      URL.revokeObjectURL(url);
      resolve({ width: w, height: h, durationS: d });
    };
    el.preload = 'metadata';
    el.onloadedmetadata = () => done(
      el.videoWidth || null, el.videoHeight || null,
      Number.isFinite(el.duration) ? el.duration : null);
    el.onerror = () => done(null, null, null);
    el.src = url;
  });
}

async function probeCodec(file: File): Promise<'h264' | 'hevc' | 'other' | null> {
  try {
    const moov = await findMoov(file);
    if (!moov) return null;
    // moov is small (KB–low MB); cap the read defensively.
    const end = Math.min(moov.end, moov.start + 4 * 1024 * 1024);
    const s = ascii(new Uint8Array(await file.slice(moov.start, end).arrayBuffer()));
    if (s.includes('hvc1') || s.includes('hev1')) return 'hevc';
    if (s.includes('avc1') || s.includes('avc3')) return 'h264';
    return 'other';
  } catch {
    return null;
  }
}

// Walk top-level MP4 boxes to locate `moov` (works whether moov is at the front
// [faststart] or the end, and never reads the giant `mdat`).
async function findMoov(file: File): Promise<{ start: number; end: number } | null> {
  let off = 0;
  const size = file.size;
  for (let i = 0; i < 64 && off + 8 <= size; i++) {
    const hdr = new DataView(await file.slice(off, off + 16).arrayBuffer());
    let boxSize = hdr.getUint32(0);
    const type = ascii(new Uint8Array(hdr.buffer, 4, 4));
    let headerSize = 8;
    if (boxSize === 1) {            // 64-bit largesize
      boxSize = Number(hdr.getBigUint64(8));
      headerSize = 16;
    } else if (boxSize === 0) {     // box extends to EOF
      boxSize = size - off;
    }
    if (type === 'moov') return { start: off + headerSize, end: off + boxSize };
    if (boxSize <= 0) break;
    off += boxSize;
  }
  return null;
}

function ascii(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]!);
  return s;
}
